import React, { createContext, useContext, useEffect, useState } from 'react';
import { login as loginService, logout as logoutService, getProfile } from '../services/authService';
import { User } from '../helpers/types';
import Cookies from 'js-cookie';
import { useAtom, useSetAtom } from 'jotai';
import { userAtom } from '../atoms/userAtom';
import { devicesAtom } from '../atoms/devicesAtom';
import { dateRangeAtom } from '../atoms/dataRangeAtom';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useAtom(userAtom);
  const setDevices = useSetAtom(devicesAtom);
  const setDateRange = useSetAtom(dateRangeAtom);
  const [isLoading, setIsLoading] = useState(true);

  const login = async (email: string, password: string) => {
    const { token, user } = await loginService(email, password);
    Cookies.set("auth_token", token);
    Cookies.set("session_user", email, { expires: 7 });
    Cookies.set("session_id", user.id, { expires: 7 });
    setUser(user);
  };

  const logout = async () => {
    await logoutService();
    Cookies.remove('auth_token');
    Cookies.remove('session_user');
    Cookies.remove('session_id');
    setUser(null);
    setDevices(null);
    setDateRange({to: new Date(Date.now()), from: new Date(Date.now() - 6 * 3600000)});
  };

  useEffect(() => {
    const user = getProfile();
    if (user) setUser(user);
    setIsLoading(false);
}, [setUser]);

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
